import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { fmtDateTime } from "../lib/format";
import BulkUploader from "../components/BulkUploader";
import RecentBatches from "../components/RecentBatches";
import api, { pollO365WithProgress } from "../lib/api";
import { toast } from "sonner";
import {
  UploadSimple, Envelope, ArrowsClockwise, Clipboard,
} from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";

export default function Inbox() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("emails"); // emails | upload | paste-po
  const [batchRefresh, setBatchRefresh] = useState(0);
  const nav = useNavigate();

  const load = () => api.get("/emails").then((r) => setEmails(r.data));
  useEffect(() => { load(); }, []);

  const reextractEmail = async (emailId) => {
    setLoading(true);
    try {
      await api.post(`/extractions/${emailId}/retry`);
      toast.success("Re-extraction triggered — refreshing…");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-extraction failed");
    } finally {
      setLoading(false);
    }
  };

  const pollO365 = async () => {
    setLoading(true);
    try {
      const d = await pollO365WithProgress();
      toast.success(`O365: fetched ${d.fetched ?? 0}, ingested ${d.processed ?? 0}, skipped ${d.skipped_already_ingested ?? 0}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "O365 polling failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout breadcrumb={["PO Inbox"]}>
      <div className="jde-toolbar">
        <Envelope size={14} weight="bold" />
        <span style={{ fontWeight: 700, fontSize: 13 }}>INGESTION INBOX</span>
        <div className="divider" />
        <button data-testid="poll-o365-btn" className="jde-btn" onClick={pollO365} disabled={loading}>
          <ArrowsClockwise size={12} weight="bold" /> {loading ? "Polling…" : "Poll O365"}
        </button>
        <button data-testid="tab-upload" className="jde-btn" onClick={() => setTab("upload")}>
          <UploadSimple size={12} weight="bold" /> Bulk Upload POs
        </button>
        <button data-testid="tab-paste-po" className="jde-btn" onClick={() => setTab("paste-po")}>
          <Clipboard size={12} weight="bold" /> Paste PO Email
        </button>
      </div>

      {tab === "upload" && (
        <BulkUploader
          endpoint="/po/upload"
          batchType="po"
          concurrency={3}
          title="Bulk PO Upload (PDF / XLSX / DOCX / RTF / EML)"
          accept=".pdf,.xlsx,.xlsm,.docx,.rtf,.eml,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/rtf,message/rfc822"
          fileTypeHint="Accepts .pdf, .xlsx, .docx, .rtf, .eml — structured spreadsheets (e.g. Walmart Import DC) are parsed exactly; others via AI. Change/revision POs are auto-detected."
          getNumberFromResponse={(d) => d?.po?.po_number}
          getRelatedIdFromResponse={(d) => d?.id}
          getChangeFromResponse={(d) => ({
            is_change: !!d?.po?.is_change_order,
            change_of_po_id: d?.po?.change_of_po_id,
            matches_sap_po: !!d?.po?.matches_sap_po,
          })}
          openHref={(item) => `/po/${item.related_id}`}
          onClose={() => setTab("emails")}
          onBatchSaved={() => setBatchRefresh((n) => n + 1)}
        />
      )}
      {tab === "paste-po" && <PastePOCard onDone={(id) => nav(`/po/${id}`)} onClose={() => setTab("emails")} />}

      <RecentBatches
        batchType="po"
        refreshKey={batchRefresh}
        openHref={(it) => `/po/${it.related_id}`}
      />

      <div className="jde-panel" style={{ marginTop: 12 }}>
        <div className="jde-panel-header">
          <span>Ingested Emails</span>
          <span style={{ fontSize: 11, fontWeight: 500 }} className="muted">
            Showing latest {emails.length}
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="jde-grid" data-testid="emails-grid">
            <thead>
              <tr>
                <th>Source</th>
                <th>Received</th>
                <th>From</th>
                <th>Subject</th>
                <th>Classification</th>
                <th>Status</th>
                <th>Related</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {emails.map((e) => (
                <tr key={e.id} data-testid={`email-row-${e.id}`}>
                  <td><span className="jde-badge confirmed">{e.source}</span></td>
                  <td className="muted" style={{ fontSize: 11 }}>{fmtDateTime(e.received_at || e.created_at)}</td>
                  <td className="text-mono" style={{ fontSize: 11 }}>{e.sender || "—"}</td>
                  <td style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {e.subject || "(no subject)"}
                  </td>
                  <td>
                    <span className={`jde-badge ${e.classified_as === "po" ? "confirmed" : e.classified_as === "invoice" ? "partial" : "closed"}`}>
                      {e.classified_as}
                    </span>
                  </td>
                  <td>
                    {e.processed
                      ? <span className="jde-badge shipped">Processed</span>
                      : <span className="jde-badge pending">Pending</span>}
                  </td>
                  <td>
                    {(e.po_count || 0) > 1
                      ? <Link to={`/orders?source_email=${e.id}`} className="text-mono" style={{ color: "var(--omh-primary)", fontWeight: 700 }} data-testid={`open-multi-${e.id}`}>{e.po_count} POs ↗</Link>
                      : (e.related_id && (e.classified_as === "po")
                          ? <Link to={`/po/${e.related_id}`} className="text-mono" style={{ color: "var(--omh-primary)" }}>Open PO</Link>
                          : <span className="muted">—</span>)}
                  </td>
                  <td>
                    {e.classified_as === "po" && (
                      <button
                        className="jde-btn jde-btn-sm"
                        onClick={() => reextractEmail(e.id)}
                        disabled={loading}
                        data-testid={`reextract-email-${e.id}`}
                        title="Re-run AI extraction on this email. Existing POs are kept, only missing ones are added."
                      >
                        <ArrowsClockwise size={11} weight="bold" /> Re-extract
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {emails.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: 20 }} className="muted">
                  No emails ingested yet. Poll O365 or upload a PO manually.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="jde-status-bar"><span>Rows: {emails.length}</span></div>
      </div>
    </Layout>
  );
}

function PastePOCard({ onDone, onClose }) {
  const [subject, setSubject] = useState("");
  const [sender, setSender] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!body.trim()) return toast.error("Paste the PO email body");
    setLoading(true);
    try {
      const r = await api.post("/po/paste", { subject, sender, body });
      toast.success("PO extracted");
      onDone(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="jde-panel" style={{ marginTop: 12 }}>
      <div className="jde-panel-header">
        <span>Paste PO Email Body</span>
        <button className="jde-btn jde-btn-sm" onClick={onClose} data-testid="paste-po-close">Close</button>
      </div>
      <div className="jde-panel-body">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div>
            <label className="jde-label">Subject</label>
            <input className="jde-input" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="paste-po-subject" />
          </div>
          <div>
            <label className="jde-label">Sender</label>
            <input className="jde-input" value={sender} onChange={(e) => setSender(e.target.value)} data-testid="paste-po-sender" />
          </div>
        </div>
        <div style={{ height: 8 }} />
        <label className="jde-label">Email Body</label>
        <textarea
          className="jde-textarea"
          rows={12}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          data-testid="paste-po-body"
          placeholder="Paste the full PO email body here (line items, qty, customer info, etc.)…"
        />
        <div style={{ marginTop: 10, textAlign: "right" }}>
          <button className="jde-btn jde-btn-primary" onClick={submit} disabled={loading} data-testid="paste-po-submit">
            {loading ? "Extracting…" : "Extract PO with AI"}
          </button>
        </div>
      </div>
    </div>
  );
}
